import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { useOrganisationDestroy, useOrganisationIndex } from '@jobilla/nexus-sdk'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { EditorErrorBoundary } from '../../../../editor/error-handling'
import { Button, ConfirmationDialog, ConfirmationDialogRef } from '../../../../rapid-ui'
import { SettingsSubheading } from '../../shared'

const companyRoute = getRouteApi('/_auth/company/$companyId')

export function OrganizationDelete() {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const { isLoading } = useAuth()
	const jobillaOrgId = useJobillaOrgId()
	const { companyId } = companyRoute.useParams()

	const { data: organisations } = useOrganisationIndex()
	const currentOrganisation = organisations?.find((organisation) =>
		organisation.slug === companyId || organisation.id === jobillaOrgId
	)
	// The index doesn't always contain the active org (e.g. Jobillans with many orgs),
	// so fall back to the slug — always present and still human-typable.
	const confirmationName = currentOrganisation?.name ?? companyId

	const deleteCompanyConfirmationRef = useRef<ConfirmationDialogRef>(null)

	const deleteCompanyMutation = useOrganisationDestroy({
		onSuccess: async () => {
			await navigate({ to: '/company', reloadDocument: true })
		},
	})

	const handleDeleteCompany = async () => {
		if (!confirmationName) {
			return
		}

		try {
			await deleteCompanyConfirmationRef.current?.confirm({
				title: t('organization.delete.confirmTitle', { name: confirmationName }),
				message: t('organization.delete.confirmMessage', { name: confirmationName }),
				confirmText: t('organization.delete.button'),
				destructive: true,
				requireConfirmationText: confirmationName,
			})
			await deleteCompanyMutation.mutateAsync({ organisation: jobillaOrgId })
		} catch {
			return
		}
	}
	return (
		<EditorErrorBoundary debugId={'settings-organization-delete'}>
			<SettingsSubheading>{t('organization.delete.heading')}</SettingsSubheading>
			<p className="max-w-[600px] mb-8 text-gray-600">
				{t('organization.delete.description')}
			</p>
			<ConfirmationDialog ref={deleteCompanyConfirmationRef} />
			{!isLoading && (
				<Button
					size={'normal'}
					type={'destructive'}
					onClick={handleDeleteCompany}
					disabled={!confirmationName}
					isLoading={deleteCompanyMutation.isPending}
				>
					{t('organization.delete.button')}
				</Button>
			)}
		</EditorErrorBoundary>
	)
}
